<template>
  <!-- 파워모드 오버레이 엔진 (vscode-power-mode 이식):
       ① 파티클 캔버스(트리거 지점에서 색 파티클 버스트) ② 콤보 미터(연속 타건 + 타임아웃 바)
       ③ 셰이크(콤보 임계 넘으면 콘텐츠 흔들림, 강도는 티어 비례).
       trigger(x, y)를 호출하면 발동. 흔들 대상은 기본 슬롯. -->
  <div ref="root" class="power-mode">
    <!-- 흔들리는 콘텐츠 -->
    <div ref="content" class="power-mode__content"><slot></slot></div>

    <!-- 파티클 캔버스 (루트 전체 덮음, 포인터 통과) -->
    <canvas ref="canvas" class="power-mode__canvas"></canvas>

    <!-- 콤보 미터 -->
    <transition name="power-combo">
      <div
        v-if="showCombo && combo > 0"
        class="power-mode__combo"
        :class="[`power-mode__combo--t${tier}`, { 'power-mode__combo--light': lightMode }]"
      >
        <div class="power-mode__combo-body">
          <span class="power-mode__combo-num">{{ combo }}</span>
          <span class="power-mode__combo-x">×</span>
        </div>
        <div class="power-mode__combo-label">{{ comboLabel }}</div>
        <div class="power-mode__combo-bar">
          <div ref="barFill" class="power-mode__combo-bar-fill"></div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Themeable from "vuetify/lib/mixins/themeable";

export default {
  name: "PowerMode",
  mixins: [Themeable], // isDark: 콤보 미터 대비 분기
  props: {
    // 이펙트 프리셋: particles(색종이) | fireworks(불꽃놀이) | flames(불꽃) | magic(반짝이) | heart(하트)
    preset: {
      type: String,
      default: "particles",
      validator: (v) => ["particles", "fireworks", "flames", "magic", "heart"].includes(v),
    },
    // 파티클 색 팔레트 (기본: 버츄펫 캔디 톤 — particles 프리셋에서 사용)
    colors: {
      type: Array,
      default: () => ["#ff5ea8", "#ffd34e", "#7c4dff", "#4dd0e1", "#8be36b", "#ff8a5c"],
    },
    // 트리거당 방출 파티클 수
    particleCount: { type: Number, default: 10 },
    // 중력(프레임당 vy 증가)
    gravity: { type: Number, default: 0.22 },
    // 공기저항(프레임당 속도 감쇠)
    drag: { type: Number, default: 0.92 },
    // 동시 파티클 상한 (성능 보호)
    maxParticles: { type: Number, default: 800 },
    // 파티클 크기·확산 일괄 배율 (프리셋 무관). 1=기본, >1이면 더 큼.
    scale: { type: Number, default: 1 },
    // 이 콤보부터 POWER MODE(셰이크/강화) 발동. 0이면 항상.
    comboThreshold: { type: Number, default: 8 },
    // 입력 없이 이 시간(ms) 지나면 콤보 리셋
    comboTimeout: { type: Number, default: 1200 },
    // 셰이크 on/off + 기본 강도(px)
    shake: { type: Boolean, default: true },
    shakeIntensity: { type: Number, default: 6 },
    // 콤보 미터 표시
    showCombo: { type: Boolean, default: true },
  },
  data() {
    return {
      combo: 0,
      tier: 0, // 0 빌드업 / 1 POWER / 2 SUPER / 3 MAX
    };
  },
  computed: {
    lightMode() {
      return !this.isDark;
    },
    comboLabel() {
      return ["combo", "POWER MODE!!!", "SUPER MODE!!!", "MAX POWER!!!"][this.tier];
    },
  },
  mounted() {
    this._particles = [];
    this._ctx = this.$refs.canvas.getContext("2d");
    this._raf = null;
    this._lastTrigger = 0;
    this._shakeMag = 0;
    this._dpr = window.devicePixelRatio || 1;
    this._resize();
    this._onResize = () => this._resize();
    window.addEventListener("resize", this._onResize);
    if (window.ResizeObserver) {
      this._ro = new ResizeObserver(this._onResize);
      this._ro.observe(this.$refs.root);
    }
  },
  beforeDestroy() {
    if (this._raf) cancelAnimationFrame(this._raf);
    window.removeEventListener("resize", this._onResize);
    if (this._ro) this._ro.disconnect();
  },
  methods: {
    // 외부에서 호출: (x, y)는 루트 기준 픽셀. 생략 시 중앙.
    trigger(x, y) {
      const rect = this.$refs.root.getBoundingClientRect();
      const px = x == null ? rect.width / 2 : x;
      const py = y == null ? rect.height / 2 : y;

      this.combo += 1;
      this.tier = this._tierFor(this.combo);
      this._lastTrigger = this._now();
      this._spawn(px, py);

      // POWER MODE 이상에서 셰이크 킥 (티어 비례)
      if (this.shake && this.combo >= this.comboThreshold) {
        this._shakeMag = this.shakeIntensity * (0.6 + this.tier * 0.55);
      }
      this._ensureLoop();
    },
    // 즉시 정지/리셋 (외부에서 blur 등)
    reset() {
      this.combo = 0;
      this.tier = 0;
      this._shakeMag = 0;
      if (this.$refs.content) this.$refs.content.style.transform = "";
    },
    _tierFor(c) {
      const t = this.comboThreshold || 1;
      if (c < t) return 0;
      if (c < t * 2.5) return 1;
      if (c < t * 5) return 2;
      return 3;
    },
    _now() {
      return typeof performance !== "undefined" ? performance.now() : Date.now();
    },
    _spawn(x, y) {
      // 콤보 tier(임계 이상일 때만)로 개수·크기·확산을 escalation.
      //  - MAX(tier3)에서 개수·확산을 크게 → 박스를 꽉 채운다. 낮은 콤보는 작게 유지.
      //  - size/speed 스케일링은 전부 여기서 일괄 적용(프리셋엔 boost=1 전달) → 프리셋 무관 균일.
      const tier = this.combo >= this.comboThreshold ? this.tier : 0;
      const countMult = 1 + tier; // 트리거당 방출 횟수 (MAX 4배 → 조밀하게)
      const sizeMult = this.scale * (1 + tier * 0.35); // 파티클 크기
      const reachMult = this.scale * (1 + tier * 0.7); // 확산(도달거리) — MAX에서 모서리까지
      const before = this._particles.length;
      for (let k = 0; k < countMult; k++) {
        switch (this.preset) {
          case "fireworks":
            this._spawnFireworks(x, y, 1);
            break;
          case "flames":
            this._spawnFlames(x, y, 1);
            break;
          case "magic":
            this._spawnMagic(x, y, 1);
            break;
          case "heart":
            this._spawnHearts(x, y, 1);
            break;
          default:
            this._spawnConfetti(x, y, 1);
        }
      }
      // escalation·scale을 이번에 생성된 파티클에 일괄 적용 (프리셋 무관)
      const parts = this._particles;
      for (let i = before; i < parts.length; i++) {
        const p = parts[i];
        p.size *= sizeMult;
        p.vx *= reachMult;
        p.vy *= reachMult;
      }
      // 상한 초과 시 오래된 것부터 컷
      if (parts.length > this.maxParticles) parts.splice(0, parts.length - this.maxParticles);
    },
    _pick() {
      return this.colors[(Math.random() * this.colors.length) | 0];
    },
    // particles: 색종이(사각/원) 방사형 버스트 + 중력 낙하
    _spawnConfetti(x, y, boost) {
      const parts = this._particles;
      for (let i = 0; i < this.particleCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = (1.5 + Math.random() * 5) * boost;
        parts.push({
          kind: "confetti",
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - Math.random() * 2,
          size: (2 + Math.random() * 4) * boost,
          rot: Math.random() * Math.PI,
          vr: (Math.random() - 0.5) * 0.3,
          alpha: 1,
          decay: 0.012 + Math.random() * 0.02,
          color: this._pick(),
          round: Math.random() < 0.35,
        });
      }
    },
    // fireworks: 단일 색상계열 스파크 방사 폭발 + 잔광(glow) + 궤적 + 중력 낙하
    _spawnFireworks(x, y, boost) {
      const parts = this._particles;
      const baseHue = (Math.random() * 360) | 0;
      const count = Math.round((this.particleCount + 16) * boost);
      for (let i = 0; i < count; i++) {
        const angle = (i / count) * Math.PI * 2 + Math.random() * 0.3;
        const speed = (2 + Math.random() * 4.5) * boost;
        const hue = baseHue + (Math.random() * 50 - 25);
        parts.push({
          kind: "spark",
          x,
          y,
          px: x,
          py: y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          size: 2 + Math.random() * 1.6,
          alpha: 1,
          decay: 0.016 + Math.random() * 0.02,
          color: `hsl(${hue}, 100%, ${60 + Math.random() * 20}%)`,
        });
      }
    },
    // flames: 불꽃 팔레트, 부력으로 위로 솟으며 흔들리고 축소·소멸
    _spawnFlames(x, y, boost) {
      const parts = this._particles;
      const fire = ["#ffe259", "#ffb038", "#ff7a18", "#ff3d2e", "#ffd34e"];
      const n = Math.round(this.particleCount * boost);
      for (let i = 0; i < n; i++) {
        parts.push({
          kind: "flame",
          x: x + (Math.random() * 12 - 6),
          y,
          vx: (Math.random() * 2 - 1) * 0.8,
          vy: -(1 + Math.random() * 2.4) * boost,
          size: (5 + Math.random() * 6) * boost,
          alpha: 0.9,
          decay: 0.02 + Math.random() * 0.02,
          color: fire[(Math.random() * fire.length) | 0],
          flick: Math.random() * Math.PI * 2,
        });
      }
    },
    // magic: 파스텔/화이트 반짝이(4점 별), 느리게 떠오르며 twinkle
    _spawnMagic(x, y, boost) {
      const parts = this._particles;
      const star = ["#ffffff", "#e6b8ff", "#b79cff", "#ffd6f5", "#9fe8ff"];
      const n = Math.round(this.particleCount * boost);
      for (let i = 0; i < n; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 0.4 + Math.random() * 1.8;
        parts.push({
          kind: "star",
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 0.4,
          size: (3 + Math.random() * 4) * boost,
          rot: Math.random() * Math.PI,
          vr: (Math.random() - 0.5) * 0.2,
          phase: Math.random() * Math.PI * 2,
          alpha: 1,
          decay: 0.008 + Math.random() * 0.012,
          color: star[(Math.random() * star.length) | 0],
        });
      }
    },
    // heart: 하트가 위로 떠오르며 좌우로 흔들림(좋아요 버스트), 핑크/레드 팔레트
    _spawnHearts(x, y, boost) {
      const parts = this._particles;
      const pal = ["#ff5ea8", "#ff3d6e", "#ff8fab", "#ff2d55", "#ffb3c8"];
      const n = Math.max(1, Math.round(this.particleCount * 0.7 * boost));
      for (let i = 0; i < n; i++) {
        parts.push({
          kind: "heart",
          x: x + (Math.random() * 16 - 8),
          y,
          vx: (Math.random() * 2 - 1) * 0.6,
          vy: -(1 + Math.random() * 1.8) * boost, // 위로 떠오름
          size: (10 + Math.random() * 8) * boost,
          rot: (Math.random() * 2 - 1) * 0.3,
          vr: (Math.random() - 0.5) * 0.04,
          sway: Math.random() * Math.PI * 2,
          alpha: 1,
          decay: 0.008 + Math.random() * 0.01,
          color: pal[(Math.random() * pal.length) | 0],
        });
      }
    },
    // 하트 모양 (베지어 2개 로브 + 아래 꼭짓점)
    _drawHeart(ctx, x, y, s, rot) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rot);
      ctx.beginPath();
      ctx.moveTo(0, s * 0.4);
      ctx.bezierCurveTo(-s * 0.62, -s * 0.05, -s * 0.5, -s * 0.5, 0, -s * 0.16);
      ctx.bezierCurveTo(s * 0.5, -s * 0.5, s * 0.62, -s * 0.05, 0, s * 0.4);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    },
    // 파티클 하나 그리기 (kind별). glow 계열은 lighter 합성으로 발광.
    _draw(ctx, p) {
      const glow = p.kind === "spark" || p.kind === "flame" || p.kind === "star";
      ctx.globalCompositeOperation = glow ? "lighter" : "source-over";
      ctx.globalAlpha = Math.max(0, Math.min(1, p.alpha));
      ctx.fillStyle = p.color;
      ctx.strokeStyle = p.color;
      if (p.kind === "spark") {
        ctx.lineWidth = p.size;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(p.px, p.py);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
      } else if (p.kind === "flame") {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else if (p.kind === "star") {
        this._drawStar(ctx, p.x, p.y, p.size * (0.55 + 0.45 * Math.sin(p.phase)), p.rot);
      } else if (p.kind === "heart") {
        this._drawHeart(ctx, p.x, p.y, p.size, p.rot);
      } else if (p.round) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
        ctx.restore();
      }
    },
    // 4점 반짝이 (십자 스파클 + 중심 점)
    _drawStar(ctx, x, y, r, rot) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rot);
      ctx.lineWidth = Math.max(1, r * 0.18);
      ctx.lineCap = "round";
      ctx.beginPath();
      for (let k = 0; k < 4; k++) {
        const ang = (k / 4) * Math.PI * 2;
        ctx.moveTo(0, 0);
        ctx.lineTo(Math.cos(ang) * r, Math.sin(ang) * r);
      }
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, 0, Math.max(1, r * 0.16), 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    },
    _resize() {
      const el = this.$refs.root;
      const cv = this.$refs.canvas;
      if (!el || !cv) return;
      const w = el.clientWidth;
      const h = el.clientHeight;
      this._dpr = window.devicePixelRatio || 1;
      cv.width = w * this._dpr;
      cv.height = h * this._dpr;
      cv.style.width = w + "px";
      cv.style.height = h + "px";
      this._w = w;
      this._h = h;
    },
    _ensureLoop() {
      if (!this._raf) this._raf = requestAnimationFrame(this._tick);
    },
    _tick() {
      const ctx = this._ctx;
      const dpr = this._dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, this._w, this._h);

      // ── 파티클 업데이트/드로우 (kind별 물리) ──
      const parts = this._particles;
      const g = this.gravity;
      for (let i = parts.length - 1; i >= 0; i--) {
        const p = parts[i];
        let dead;
        if (p.kind === "spark") {
          p.px = p.x;
          p.py = p.y;
          p.vx *= 0.9;
          p.vy = p.vy * 0.9 + g * 0.55;
          p.x += p.vx;
          p.y += p.vy;
          p.alpha -= p.decay;
          dead = p.alpha <= 0;
        } else if (p.kind === "flame") {
          p.vy = p.vy * 0.96 - 0.06; // 부력(위로 상승)
          p.flick += 0.3;
          p.vx = p.vx * 0.92 + Math.sin(p.flick) * 0.15;
          p.x += p.vx;
          p.y += p.vy;
          p.size *= 0.95;
          p.alpha -= p.decay;
          dead = p.alpha <= 0 || p.size < 0.6;
        } else if (p.kind === "star") {
          p.vx *= 0.97;
          p.vy = p.vy * 0.97 - 0.015; // 천천히 떠오름
          p.x += p.vx;
          p.y += p.vy;
          p.rot += p.vr;
          p.phase += 0.22;
          p.alpha -= p.decay;
          dead = p.alpha <= 0;
        } else if (p.kind === "heart") {
          p.sway += 0.08;
          p.vy = p.vy * 0.98 - 0.02; // 부력(계속 상승)
          p.vx = p.vx * 0.96 + Math.sin(p.sway) * 0.12; // 좌우 흔들림
          p.x += p.vx;
          p.y += p.vy;
          p.rot += p.vr;
          p.alpha -= p.decay;
          dead = p.alpha <= 0;
        } else {
          // confetti
          p.vx *= this.drag;
          p.vy = p.vy * this.drag + g;
          p.x += p.vx;
          p.y += p.vy;
          p.rot += p.vr;
          p.alpha -= p.decay;
          dead = p.alpha <= 0;
        }
        if (dead) {
          parts.splice(i, 1);
          continue;
        }
        this._draw(ctx, p);
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "source-over";

      // ── 콤보 타이머 바 + 리셋 ──
      let comboActive = this.combo > 0;
      if (comboActive) {
        const elapsed = this._now() - this._lastTrigger;
        const frac = 1 - elapsed / this.comboTimeout;
        if (frac <= 0) {
          this.reset();
          comboActive = false;
        } else if (this.$refs.barFill) {
          this.$refs.barFill.style.transform = `scaleX(${frac})`;
        }
      }

      // ── 셰이크 (콘텐츠 트랜스폼) ──
      if (this._shakeMag > 0.3) {
        const m = this._shakeMag;
        const dx = (Math.random() * 2 - 1) * m;
        const dy = (Math.random() * 2 - 1) * m;
        this.$refs.content.style.transform = `translate(${dx}px, ${dy}px)`;
        this._shakeMag *= 0.85; // 감쇠
      } else if (this._shakeMag !== 0) {
        this._shakeMag = 0;
        this.$refs.content.style.transform = "";
      }

      // 계속 돌지 정지할지
      if (parts.length || comboActive || this._shakeMag) {
        this._raf = requestAnimationFrame(this._tick);
      } else {
        this._raf = null;
      }
    },
  },
};
</script>

<style scoped>
.power-mode {
  position: relative;
  width: 100%;
}
.power-mode__content {
  position: relative;
  z-index: 1;
  will-change: transform;
}
.power-mode__canvas {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
}

/* ── 콤보 미터 (우상단 플로팅) ── */
.power-mode__combo {
  position: absolute;
  top: -14px;
  right: -6px;
  z-index: 3;
  pointer-events: none;
  text-align: right;
  font-family: maplestory, sans-serif;
  user-select: none;
}
.power-mode__combo-body {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  line-height: 1;
}
.power-mode__combo-num {
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -1px;
  color: #cbb8ff;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}
.power-mode__combo-x {
  font-size: 18px;
  font-weight: 800;
  color: #9a93ad;
  margin-left: 2px;
}
.power-mode__combo-label {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: #9a93ad;
  margin-top: 1px;
  text-transform: uppercase;
}
.power-mode__combo-bar {
  width: 68px;
  height: 4px;
  margin: 4px 0 0 auto;
  border-radius: 999px;
  background: rgba(128, 128, 128, 0.25);
  overflow: hidden;
}
.power-mode__combo-bar-fill {
  width: 100%;
  height: 100%;
  border-radius: 999px;
  background: #cbb8ff;
  transform-origin: right center; /* 오른쪽에서 줄어듦 */
  transform: scaleX(1);
}

/* ── 티어별 색/스케일 escalation ── */
.power-mode__combo--t1 .power-mode__combo-num,
.power-mode__combo--t1 .power-mode__combo-label {
  color: #ffb038;
}
.power-mode__combo--t1 .power-mode__combo-bar-fill {
  background: #ffb038;
}
.power-mode__combo--t2 .power-mode__combo-num,
.power-mode__combo--t2 .power-mode__combo-label {
  color: #ff5a5f;
}
.power-mode__combo--t2 .power-mode__combo-bar-fill {
  background: #ff5a5f;
}
.power-mode__combo--t2 .power-mode__combo-num {
  font-size: 40px;
}
.power-mode__combo--t3 .power-mode__combo-num {
  font-size: 46px;
  background: linear-gradient(90deg, #ff5ea8, #ffd34e, #7c4dff, #4dd0e1);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: power-hue 1.2s linear infinite;
}
.power-mode__combo--t3 .power-mode__combo-label {
  color: #ff5ea8;
}
.power-mode__combo--t3 .power-mode__combo-bar-fill {
  background: linear-gradient(90deg, #ff5ea8, #ffd34e, #7c4dff, #4dd0e1);
}
@keyframes power-hue {
  to {
    filter: hue-rotate(360deg);
  }
}

/* 콤보 숫자 등장 시 팝 */
.power-combo-enter-active {
  transition: transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.18s ease;
}
.power-combo-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.power-combo-enter,
.power-combo-leave-to {
  opacity: 0;
  transform: translateY(6px) scale(0.7);
}

/* light 테마: 미터 기본색만 조정 */
.power-mode__combo--light .power-mode__combo-num {
  color: #7c4dff;
}
.power-mode__combo--light .power-mode__combo-label,
.power-mode__combo--light .power-mode__combo-x {
  color: #8a84a0;
}
.power-mode__combo--light .power-mode__combo-bar-fill {
  background: #7c4dff;
}
</style>
